import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { listIntegrations } from "../api/integrations";
import {
  fetchUserWorkgroups,
  fetchChallengeById,
  fetchDisplayChallengeById,
} from "./firestoreFunctions";
import { firestore, getToken, getUserId } from "../firebase";
import { WorkgroupSelect } from "./WorkgroupSelect";
import StatusTimeline from "./StatusTimeline";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Button } from "./ui/button";
import {
  Upload,
  Play,
  RotateCcw,
  CheckCircle,
  AlertCircle,
  Clock,
  Brain,
  Zap,
  History,
  Home,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import ImageUploadDropzone from "./ImageUploadDropzone";
import ActivityMessage, { isJsonContent, parseActivityMessage } from "./ActivityMessage";

const STEP_ORDER = [
  "SETUP",
  "STARTING",
  "GAP_DETECTION",
  "EXPLORATION",
  "LEARNING",
  "VERIFICATION",
  "STORING",
  "COMPLETED",
  "FAILED",
];

const CDDIDemo = () => {
  const navigate = useNavigate();

  const demoStepsConfig = useMemo(
    () => [
      {
        id: "SETUP",
        title: "Challenge Setup",
        description: "Initializing challenge and preparing system",
        status: "pending",
        icon: AlertCircle,
      },
      {
        id: "STARTING",
        title: "System Starting",
        description: "CDDI system initializing and loading resources",
        status: "pending",
        icon: Clock,
      },
      {
        id: "GAP_DETECTION",
        title: "Knowledge Gap Detection",
        description: "Analyzing challenge and identifying knowledge gaps",
        status: "pending",
        icon: Brain,
      },
      {
        id: "EXPLORATION",
        title: "AI Exploration Begins",
        description: "ExplorationAgent conducts autonomous learning",
        status: "pending",
        icon: Zap,
      },
      {
        id: "LEARNING",
        title: "Knowledge Extraction",
        description: "Converting successful attempts into usable knowledge",
        status: "pending",
        icon: Clock,
      },
      {
        id: "VERIFICATION",
        title: "Knowledge Verification",
        description: "Validating discovered knowledge",
        status: "pending",
        icon: CheckCircle,
      },
      {
        id: "STORING",
        title: "Knowledge Storage",
        description: "Adding new knowledge to system",
        status: "pending",
        icon: CheckCircle,
      },
      {
        id: "COMPLETED",
        title: "Challenge Resolved",
        description: "Challenge now resolved with new knowledge",
        status: "pending",
        icon: CheckCircle,
      },
      {
        id: "FAILED",
        title: "Challenge Failed",
        description: "Challenge could not be resolved",
        status: "pending",
        icon: AlertCircle,
      },
    ],
    []
  );

  const [integrations, setIntegrations] = useState([]);
  const [selectedIntegration, setSelectedIntegration] = useState("maximo");
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [guidanceText, setGuidanceText] = useState("");
  const [maxTurns, setMaxTurns] = useState("");
  const [demoStarted, setDemoStarted] = useState(false);
  const [demoSteps, setDemoSteps] = useState(demoStepsConfig);
  const [currentStep, setCurrentStep] = useState(0);
  const [learningProgress, setLearningProgress] = useState({});
  const [challengeId, setChallengeId] = useState(null);
  const [error, setError] = useState(null);

  const [workgroups, setWorkgroups] = useState([]);
  const [selectedWorkgroup, setSelectedWorkgroup] = useState("");
  const [workgroupsLoading, setWorkgroupsLoading] = useState(true);

  const [eventSource, setEventSource] = useState(null);
  const [activityEventSource, setActivityEventSource] = useState(null);
  const [activityData, setActivityData] = useState(null);
  const [activityHistory, setActivityHistory] = useState([]);
  const [showActivityHistory, setShowActivityHistory] = useState(false);
  const [selectedActivityDetails, setSelectedActivityDetails] = useState(null);
  const [showActivityDetails, setShowActivityDetails] = useState(false);

  const [isDragging, setIsDragging] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState("");
  const [userContext, setUserContext] = useState("");

  useEffect(() => {
    const loadIntegrations = async () => {
      try {
        const data = await listIntegrations();
        setIntegrations(data);
        if (
          data.length &&
          !data.some((integration) => integration.id === "maximo")
        ) {
          setSelectedIntegration(data[0].id);
        }
      } catch (err) {
        console.error("Failed to load integrations:", err);
      }
    };

    loadIntegrations();
  }, []);

  useEffect(() => {
    let isMounted = true;
    const loadWorkgroups = async () => {
      setWorkgroupsLoading(true);
      try {
        const userId = await getUserId();
        if (!userId) {
          console.error("No authenticated user found via Keycloak");
          if (isMounted) {
            setWorkgroups([]);
          }
          return;
        }

        const userWorkgroups = await fetchUserWorkgroups(userId);
        const workgroupObjects = userWorkgroups.map((id) => ({
          id,
          name: id,
        }));

        if (isMounted) {
          setWorkgroups(workgroupObjects);
          if (workgroupObjects.length) {
            setSelectedWorkgroup((current) => {
              if (
                current &&
                workgroupObjects.some((workgroup) => workgroup.id === current)
              ) {
                return current;
              }
              return workgroupObjects[0].id;
            });
          }
        }
      } catch (err) {
        console.error("Failed to load workgroups:", err);
        if (isMounted) {
          setWorkgroups([]);
        }
      } finally {
        if (isMounted) {
          setWorkgroupsLoading(false);
        }
      }
    };

    loadWorkgroups();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    setDemoSteps(demoStepsConfig);
  }, [demoStepsConfig]);

  useEffect(() => {
    if (!challengeId || !selectedIntegration) return;
    let cancelled = false;

    const loadPrompt = async () => {
      try {
        const live = await fetchChallengeById(challengeId);
        if (!cancelled && live?.system_prompt) {
          setSystemPrompt(live.system_prompt);
          setUserContext(live.user_prompt_context || "");
          return;
        }

        const display = await fetchDisplayChallengeById(
          selectedIntegration,
          challengeId
        );
        if (!cancelled && display?.system_prompt) {
          setSystemPrompt(display.system_prompt);
          setUserContext(display.user_prompt_context || "");
        }
      } catch (err) {
        console.warn("Failed to load system prompt:", err);
      }
    };

    loadPrompt();
    return () => {
      cancelled = true;
    };
  }, [challengeId, selectedIntegration]);

  useEffect(() => {
    if (!showImageModal) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setShowImageModal(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showImageModal]);

  useEffect(() => {
    const onPaste = (event) => {
      const items = event.clipboardData?.items;
      if (!items?.length) return;
      for (let index = 0; index < items.length; index += 1) {
        const item = items[index];
        if (item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) {
            processSelectedFile(file);
            event.preventDefault();
            break;
          }
        }
      }
    };

    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, []);

  useEffect(
    () => () => {
      eventSource?.close();
    },
    [eventSource]
  );

  useEffect(
    () => () => {
      activityEventSource?.close();
    },
    [activityEventSource]
  );

  const processSelectedFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Please select an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("Image file size must be less than 5MB");
      return;
    }
    setSelectedImage(file);
    setError(null);

    const reader = new FileReader();
    reader.onload = (event) => {
      const value = event.target?.result;
      setImagePreview(typeof value === "string" ? value : null);
    };
    reader.readAsDataURL(file);
  };

  const handleImageSelect = (event) => {
    const file = event.target.files?.[0] ?? null;
    processSelectedFile(file);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isDragging) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    const file = event.dataTransfer?.files?.[0] ?? null;
    processSelectedFile(file);
  };

  const updateStepsFromChallengeStep = useCallback(
    (status) => {
      const normalized = status.replace("ChallengeStep.", "");
      const activeIndex = STEP_ORDER.indexOf(normalized);
      setCurrentStep(activeIndex >= 0 ? activeIndex : 0);
      setDemoSteps((previous) =>
        previous.map((step, index) => {
          if (index < activeIndex) {
            return { ...step, status: "completed" };
          }
          if (index === activeIndex) {
            if (normalized === "FAILED") {
              return { ...step, status: "failed" };
            }
            return { ...step, status: "active" };
          }
          return { ...step, status: "pending" };
        })
      );
    },
    []
  );

  const startActivityEventSource = useCallback(
    async (challenge) => {
      if (activityEventSource) {
        activityEventSource.close();
      }

      try {
        const token = await getToken();
        const llmUrl = process.env.REACT_APP_LLM_URL || "http://localhost:5001";
        const url = `${llmUrl}/v1/challenge_activity/${challenge}?token=${encodeURIComponent(
          token || ""
        )}`;

        const source = new EventSource(url);
        setActivityEventSource(source);

        source.onmessage = (event) => {
          const parsed = parseActivityMessage(event.data);
          setActivityData(parsed);
          setActivityHistory((previous) => [
            ...previous,
            {
              id: Date.now(),
              raw: event.data,
              timestamp: new Date().toISOString(),
            },
          ]);
        };

        source.onerror = (err) => {
          console.error("Activity stream error:", err);
          source.close();
          setActivityEventSource(null);
        };
      } catch (err) {
        console.error("Failed to start activity stream:", err);
        setActivityEventSource(null);
      }
    },
    [activityEventSource]
  );

  const startEventSourceMonitoring = useCallback(
    async (challenge) => {
      if (eventSource) {
        eventSource.close();
      }

      setDemoSteps(
        demoStepsConfig.map((step, index) => ({
          ...step,
          status: index === 0 ? "active" : "pending",
        }))
      );
      setCurrentStep(0);
      await startActivityEventSource(challenge);

      try {
        const token = await getToken();
        const llmUrl = process.env.REACT_APP_LLM_URL || "http://localhost:5001";
        const url = `${llmUrl}/v1/challenge_step/${challenge}?token=${encodeURIComponent(
          token || ""
        )}`;

        const source = new EventSource(url);
        setEventSource(source);

        source.onmessage = (event) => {
          const status = event.data;
          setLearningProgress((previous) => ({
            ...previous,
            current_status: status,
            timestamp: new Date().toISOString(),
          }));
          updateStepsFromChallengeStep(status);

          const normalized = status.replace("ChallengeStep.", "");
          if (normalized === "COMPLETED" || normalized === "FAILED") {
            source.close();
            setEventSource(null);
          }
        };

        source.onerror = (err) => {
          console.error("Challenge stream error:", err);
          source.close();
          setEventSource(null);
          setError("Connection lost - please refresh the page.");
        };
      } catch (err) {
        console.error("Failed to start challenge stream:", err);
        setError("Unable to connect to status stream.");
      }
    },
    [demoStepsConfig, eventSource, startActivityEventSource, updateStepsFromChallengeStep]
  );

  const resetDemo = useCallback(() => {
    setDemoStarted(false);
    setCurrentStep(0);
    setDemoSteps(demoStepsConfig);
    setLearningProgress({});
    setChallengeId(null);
    setActivityData(null);
    setActivityHistory([]);
    setShowActivityHistory(false);
    setSelectedActivityDetails(null);
    setSystemPrompt("");
    setUserContext("");

    if (eventSource) {
      eventSource.close();
      setEventSource(null);
    }
    if (activityEventSource) {
      activityEventSource.close();
      setActivityEventSource(null);
    }
  }, [activityEventSource, demoStepsConfig, eventSource]);

  const startDemo = useCallback(async () => {
    if (!selectedWorkgroup) {
      setError("Please select a workgroup first");
      return;
    }
    if (!selectedImage && (!guidanceText || guidanceText.trim().length === 0)) {
      setError("Please add a screenshot or guidance first");
      return;
    }

    setDemoStarted(true);
    setCurrentStep(0);
    setError(null);
    setActivityHistory([]);
    setSelectedActivityDetails(null);

    const parsedMaxTurns =
      maxTurns.trim() !== "" ? parseInt(maxTurns.trim(), 10) : null;
    if (
      parsedMaxTurns !== null &&
      (Number.isNaN(parsedMaxTurns) || parsedMaxTurns <= 0)
    ) {
      setError("Max turns must be a positive integer.");
      setDemoStarted(false);
      return;
    }

    const basePayload = {
      integration_id: selectedIntegration,
      workgroup_id: selectedWorkgroup,
      goal:
        "Learn about this Maximo feature and provide guidance on how to use it.",
    };

    if (guidanceText.trim().length) {
      basePayload.guidance = guidanceText.trim();
    }
    if (parsedMaxTurns && parsedMaxTurns > 0) {
      basePayload.max_turns = parsedMaxTurns;
    }

    const sendChallenge = async (payload) => {
      try {
        const response = await fetch("/api/llm/v1/challenges", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${await getToken()}`,
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error(`Failed to create challenge: ${response.status}`);
        }

        const data = await response.json();
        setChallengeId(data.challenge_id);
        await startEventSourceMonitoring(data.challenge_id);
      } catch (err) {
        console.error("Error creating challenge:", err);
        setError(`Failed to start demo: ${err.message || "Unknown error"}`);
        setDemoStarted(false);
      }
    };

    try {
      if (selectedImage) {
        const reader = new FileReader();
        reader.onload = async (event) => {
          const value = event.target?.result;
          if (typeof value === "string") {
            const [, data = ""] = value.split(",");
            await sendChallenge({
              ...basePayload,
              screenshot_base64: data,
            });
          } else {
            await sendChallenge(basePayload);
          }
        };
        reader.readAsDataURL(selectedImage);
      } else {
        await sendChallenge(basePayload);
      }
    } catch (err) {
      console.error("Error creating challenge:", err);
      setError(`Failed to start demo: ${err.message || "Unknown error"}`);
      setDemoStarted(false);
    }
  }, [
    guidanceText,
    maxTurns,
    selectedImage,
    selectedIntegration,
    selectedWorkgroup,
    startEventSourceMonitoring,
  ]);

  const timelineItems = useMemo(
    () =>
      demoSteps.map((step, index) => ({
        id: step.id,
        title: step.title,
        description: step.description,
        status: step.status,
        icon: step.icon,
        meta: `Step ${index + 1} of ${demoSteps.length}`,
      })),
    [demoSteps]
  );

  const overviewItems = useMemo(
    () => [
      {
        label: "Current step",
        value: demoStarted
          ? `Step ${Math.min(currentStep + 1, demoSteps.length)} of ${
              demoSteps.length
            }`
          : "Not started",
      },
      {
        label: "Last update",
        value: learningProgress.timestamp
          ? new Date(learningProgress.timestamp).toLocaleTimeString()
          : "—",
      },
      { label: "Challenge ID", value: challengeId || "Not created yet" },
      { label: "Integration", value: selectedIntegration || "—" },
      { label: "Status stream", value: eventSource ? "Streaming" : "Idle" },
      {
        label: "Activity stream",
        value: activityEventSource ? "Streaming" : "Idle",
      },
    ],
    [
      activityEventSource,
      challengeId,
      currentStep,
      demoStarted,
      demoSteps.length,
      eventSource,
      learningProgress.timestamp,
      selectedIntegration,
    ]
  );

  const startDisabled =
    demoStarted ||
    workgroupsLoading ||
    !selectedWorkgroup ||
    (!selectedImage && (!guidanceText || guidanceText.trim().length === 0));

  const heroHighlights = useMemo(
    () => [
      {
        title: "Autonomous workflow",
        description: "Watch the learning phases update in real time.",
        icon: Brain,
      },
      {
        title: "Live activity feed",
        description: "Monitor each agent action as the challenge progresses.",
        icon: Zap,
      },
      {
        title: "Transparent context",
        description: "Review prompts and user context while the run executes.",
        icon: CheckCircle,
      },
    ],
    []
  );

  return (
    <div className="space-y-8 px-6 pb-16">
      <section
        className="relative overflow-hidden rounded-2xl border bg-gradient-to-br from-primary/10 via-background to-background p-6 shadow-sm"
        data-testid="cddi-demo-hero"
      >
        <div
          className="pointer-events-none absolute -top-24 right-12 h-48 w-48 rounded-full bg-primary/10 blur-3xl"
          aria-hidden="true"
        />
        <div
          className="pointer-events-none absolute -bottom-24 left-16 h-56 w-56 rounded-full bg-blue-500/10 blur-3xl"
          aria-hidden="true"
        />
        <div className="relative space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="space-y-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Autonomous learning demo
              </div>
              <h1 className="text-3xl font-semibold tracking-tight">
                CDDI challenge console
              </h1>
              <p className="max-w-2xl text-sm text-muted-foreground">
                Monitor how the platform investigates a previously unknown
                workflow. Configure the challenge, follow the learning phases,
                and inspect the resulting context in one place.
              </p>
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                size="sm"
                onClick={() =>
                  document
                    .getElementById("cddi-demo-setup-card")
                    ?.scrollIntoView({ behavior: "smooth", block: "start" })
                }
                className="inline-flex items-center"
                data-testid="cddi-demo-scroll-to-setup"
              >
                <Play className="mr-2 h-4 w-4" />
                Prepare the challenge
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/")}
                className="inline-flex items-center"
                data-testid="cddi-demo-back-home"
              >
                <Home className="mr-2 h-4 w-4" />
                Back to home
              </Button>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {heroHighlights.map((item) => (
              <div
                key={item.title}
                className="rounded-xl border border-border/60 bg-background/70 p-4 shadow-sm backdrop-blur"
              >
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <item.icon className="h-4 w-4 text-primary" />
                  {item.title}
                </div>
                <p className="mt-2 text-sm text-muted-foreground">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="grid gap-6 2xl:grid-cols-[380px,1fr]">
        <div className="space-y-6">
          <Card id="cddi-demo-setup-card" data-testid="cddi-demo-setup-card">
            <CardHeader className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <Upload className="h-4 w-4 text-primary" />
                Challenge setup
              </div>
              <CardTitle>Configure the learning run</CardTitle>
              <CardDescription>
                Select the workgroup, integration, and optional assets before
                launching the agents.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {error && (
                <div
                  className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
                  role="alert"
                >
                  <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div className="space-y-2">
                <label className="text-sm font-medium">Workgroup</label>
                {workgroupsLoading ? (
                  <div className="flex h-10 items-center justify-center rounded-md border border-border bg-muted/30 px-3 text-sm text-muted-foreground">
                    Loading workgroups…
                  </div>
                ) : (
                  <WorkgroupSelect
                    selectedWorkgroup={selectedWorkgroup}
                    onSelect={setSelectedWorkgroup}
                    workgroups={workgroups.length ? workgroups : undefined}
                    dataTestId="workgroup-select-cddi-demo"
                    placeholder="Choose a workgroup"
                    emptyMessage="No workgroups available"
                  />
                )}
              </div>

              <div className="space-y-2">
                <label
                  className="text-sm font-medium"
                  htmlFor="cddi-demo-integration"
                >
                  Integration
                </label>
                <select
                  id="cddi-demo-integration"
                  value={selectedIntegration}
                  onChange={(event) => setSelectedIntegration(event.target.value)}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  data-testid="cddi-demo-integration-select"
                >
                  <option value="" disabled>
                    Select an integration
                  </option>
                  {integrations.map((integration) => (
                    <option key={integration.id} value={integration.id}>
                      {integration.name} ({integration.id})
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Screenshot (optional)</label>
                <ImageUploadDropzone
                  onDragOver={handleDragOver}
                  onDragEnter={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onChange={handleImageSelect}
                  className={`h-32 ${
                    isDragging
                      ? "border-primary bg-primary/5"
                      : "border-muted-foreground/40 hover:border-primary"
                  }`}
                  dataTestId="cddi-demo-dropzone"
                >
                  <div className="flex flex-col items-center justify-center gap-2 pt-5 pb-6 text-center">
                    <Upload className="h-5 w-5 text-muted-foreground" />
                    <div className="text-center text-xs text-muted-foreground">
                      <p className="font-medium">
                        Drag &amp; drop or click to upload
                      </p>
                      <p>PNG or JPG up to 5MB</p>
                    </div>
                  </div>
                </ImageUploadDropzone>
                {imagePreview && (
                  <div className="rounded-md border border-border bg-muted/30 p-3">
                    <p className="mb-2 text-xs font-medium text-muted-foreground">
                      Preview
                    </p>
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="max-h-48 w-full cursor-zoom-in rounded-md border border-border"
                      onClick={() => setShowImageModal(true)}
                    />
                  </div>
                )}
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium" htmlFor="guidance">
                    Guidance (optional)
                  </label>
                  <textarea
                    id="guidance"
                    value={guidanceText}
                    onChange={(event) => setGuidanceText(event.target.value)}
                    placeholder="Provide extra context for the agents…"
                    className="min-h-[120px] w-full rounded-md border border-border bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    data-testid="cddi-demo-guidance"
                  />
                  <p className="text-xs text-muted-foreground">
                    Add domain context or desired outcomes to guide exploration.
                  </p>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium" htmlFor="max-turns">
                    Max turns (optional)
                  </label>
                  <input
                    id="max-turns"
                    type="number"
                    min="1"
                    value={maxTurns}
                    onChange={(event) => setMaxTurns(event.target.value)}
                    placeholder="Default"
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    data-testid="cddi-demo-max-turns"
                  />
                  <p className="text-xs text-muted-foreground">
                    Leave blank to use the integration default.
                  </p>
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex flex-wrap gap-2">
              <Button
                onClick={startDemo}
                disabled={startDisabled}
                className="flex-1 sm:flex-none"
                data-testid="cddi-demo-start"
              >
                <Play className="mr-2 h-4 w-4" />
                Start demo
              </Button>
              <Button
                variant="outline"
                onClick={resetDemo}
                className="flex-1 sm:flex-none"
                data-testid="cddi-demo-reset"
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                Reset
              </Button>
            </CardFooter>
          </Card>

          <Card data-testid="cddi-demo-run-overview">
            <CardHeader className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <Clock className="h-4 w-4 text-primary" />
                Run overview
              </div>
              <CardDescription>
                Key diagnostics for the active session.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 text-sm sm:grid-cols-2">
              {overviewItems.map((item) => (
                <div key={item.label} className="space-y-1">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">
                    {item.label}
                  </p>
                  <p className="break-words font-medium">{item.value}</p>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card data-testid="cddi-demo-timeline">
            <CardHeader>
              <CardTitle>Learning timeline</CardTitle>
              <CardDescription>
                Track each stage of the challenge lifecycle.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <StatusTimeline items={timelineItems} />
            </CardContent>
          </Card>

          <Card data-testid="cddi-demo-activity">
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <div>
                <CardTitle>Live activity</CardTitle>
                <CardDescription>
                  Realtime updates from the demo run.
                </CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => challengeId && startActivityEventSource(challengeId)}
                disabled={!challengeId}
                className="inline-flex items-center"
                data-testid="cddi-demo-activity-refresh"
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                Refresh
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {demoStarted ? (
                <>
                  <div
                    className={`rounded-md border border-border bg-muted/20 p-4 ${
                      activityData?.hasDetails ? "cursor-pointer hover:bg-muted/30" : ""
                    }`}
                    role={activityData?.hasDetails ? "button" : undefined}
                    tabIndex={activityData?.hasDetails ? 0 : undefined}
                    onClick={() => {
                      if (activityData?.hasDetails) {
                        setSelectedActivityDetails(activityData.details);
                        setShowActivityDetails(true);
                      }
                    }}
                    onKeyDown={(event) => {
                      if (!activityData?.hasDetails) return;
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        setSelectedActivityDetails(activityData.details);
                        setShowActivityDetails(true);
                      }
                    }}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-sm font-medium">
                          {activityData?.activity ||
                            learningProgress.current_status ||
                            "Initializing..."}
                        </p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {activityData?.hasDetails
                            ? "Click to view detailed output."
                            : "Monitoring the learning agents for progress."}
                        </p>
                      </div>
                      <History className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" />
                        {learningProgress.timestamp
                          ? new Date(learningProgress.timestamp).toLocaleTimeString()
                          : "Awaiting first update"}
                      </span>
                      <span className="flex items-center gap-1">
                        <Brain className="h-3.5 w-3.5" />
                        {selectedIntegration || "—"}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="text-sm text-muted-foreground">
                      {showActivityHistory
                        ? "Hide the detailed activity events."
                        : "View the detailed activity events."}
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowActivityHistory(!showActivityHistory)}
                    >
                      {showActivityHistory ? "Hide history" : "View history"}
                    </Button>
                  </div>

                  {showActivityHistory && (
                    <div className="max-h-64 space-y-2 overflow-y-auto rounded-md border bg-background p-3 text-sm">
                      {activityHistory.length ? (
                        activityHistory.map((activity) => (
                          <ActivityMessage
                            key={activity.id}
                            message={activity.raw}
                            timestamp={activity.timestamp}
                            variant="demo"
                            onSelect={(parsed) => {
                              setSelectedActivityDetails(parsed.details);
                              setShowActivityDetails(true);
                            }}
                          />
                        ))
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          Activity will appear here once the run begins.
                        </p>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
                  Start the demo to stream live activity updates.
                </div>
              )}
            </CardContent>
          </Card>

          {(systemPrompt || userContext) && (
            <Card data-testid="cddi-demo-prompts">
              <CardHeader>
                <CardTitle>Prompt &amp; context</CardTitle>
                <CardDescription>
                  Inputs supplied to the learning agents for this demo.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                {systemPrompt && (
                  <section className="space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <h4 className="font-semibold">System prompt</h4>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigator.clipboard.writeText(systemPrompt)}
                      >
                        Copy
                      </Button>
                    </div>
                    <pre className="max-h-48 overflow-y-auto whitespace-pre-wrap rounded-md border bg-muted/40 p-3 text-xs">
                      {systemPrompt}
                    </pre>
                  </section>
                )}
                {userContext && (
                  <section className="space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <h4 className="font-semibold">User context</h4>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigator.clipboard.writeText(userContext)}
                      >
                        Copy
                      </Button>
                    </div>
                    <pre className="max-h-40 overflow-y-auto whitespace-pre-wrap rounded-md border bg-muted/40 p-3 text-xs">
                      {userContext}
                    </pre>
                  </section>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {showActivityDetails && selectedActivityDetails && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <div className="relative mx-4 w-full max-w-3xl rounded-xl border border-border bg-background p-6 shadow-lg">
            <button
              onClick={() => {
                setShowActivityDetails(false);
                setSelectedActivityDetails(null);
              }}
              className="absolute right-4 top-4 text-muted-foreground hover:text-foreground"
              aria-label="Close activity details"
            >
              ✕
            </button>
            <CardHeader className="px-0 pt-0">
              <CardTitle>Activity details</CardTitle>
              <CardDescription>Snapshot of the most recent agent output.</CardDescription>
            </CardHeader>
            <CardContent className="px-0">
              {isJsonContent(selectedActivityDetails) ? (
                <pre className="max-h-[60vh] overflow-y-auto whitespace-pre-wrap rounded-md border bg-muted/40 p-4 text-xs">
                  <code>{selectedActivityDetails}</code>
                </pre>
              ) : (
                <div className="max-h-[60vh] overflow-y-auto whitespace-pre-wrap rounded-md border bg-muted/40 p-4 text-sm">
                  {selectedActivityDetails}
                </div>
              )}
            </CardContent>
          </div>
        </div>
      )}

      {showImageModal && imagePreview && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
          onClick={() => setShowImageModal(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Enlarged preview image"
        >
          <div
            className="relative mx-4 max-h-[85vh] max-w-[90vw]"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              onClick={() => setShowImageModal(false)}
              className="absolute -right-3 -top-3 flex h-8 w-8 items-center justify-center rounded-full bg-background text-foreground shadow"
              aria-label="Close image"
            >
              ✕
            </button>
            <img
              src={imagePreview}
              alt="Enlarged preview"
              className="max-h-[85vh] max-w-[90vw] rounded-lg object-contain"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CDDIDemo;
